import { gql, useQuery, useSubscription } from '@apollo/client';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import logger from '@orn/shared-web/lib/logger';

import { useFeatureGate } from './useFeatureGates';
import { useWsReconnectEffect } from './useWsReconnectEffect';

import type {
  FinancialAccountNamesQuery,
  FinancialAccountNamesQueryVariables,
  FinancialAccountUpdatedSubscription,
  FinancialAccountUpdatedSubscriptionVariables,
} from './__generated__/useFinancialAccountLiveUpdates.gql';

/**
 * One fragment for both the reconciliation query and the subscription: the
 * push payload must land on the same normalized `FinancialAccount` fields the
 * query reads, or the two silently desync the moment a field is added to one
 * and not the other.
 *
 * `name` is the server's display-name derivation (nickname when set,
 * otherwise the bank's canonical name) — the same field every account list
 * selects, so a pushed payload lands on the normalized entity and every
 * mounted pane showing the name re-renders in place.
 */
const FINANCIAL_ACCOUNT_LIVE_FIELDS = gql`
  fragment FinancialAccountLiveFields on FinancialAccount {
    id
    name
  }
`;

/**
 * Deliberately its own document rather than reusing the panes'
 * `GetAllFinancialAccounts`: reconciliation has to work no matter which panes
 * happen to be mounted, and a hook that refetched someone else's query would
 * reconcile nothing on a route that doesn't render accounts. The cost is one
 * extra `getFinancialAccountsForUser` round trip per session on the dashboard,
 * where the wide query runs too. They cannot drift — both land on the same
 * normalized root field, and this one selects a strict subset.
 */
const FINANCIAL_ACCOUNT_NAMES = gql`
  query FinancialAccountNames {
    getFinancialAccountsForUser {
      ...FinancialAccountLiveFields
    }
  }
  ${FINANCIAL_ACCOUNT_LIVE_FIELDS}
`;

const FINANCIAL_ACCOUNT_UPDATED_SUBSCRIPTION = gql`
  subscription FinancialAccountUpdated {
    financialAccountUpdated {
      ...FinancialAccountLiveFields
    }
  }
  ${FINANCIAL_ACCOUNT_LIVE_FIELDS}
`;

/**
 * How often the reconciliation query re-reads account names. Slow on purpose:
 * push carries the latency, and this only has to catch an event the transport
 * dropped — a NOTIFY gap or a delivery lost while the socket stayed up, which
 * no reconnect refetch would notice.
 */
const RECONCILE_POLL_INTERVAL_MS = 10 * 60 * 1000;

/**
 * Live financial-account updates for the whole app — mount once in the app
 * shell, inside the authenticated tree.
 *
 * The pilot writer is the assistant's rename tool: the agent renames an
 * account and any open pane showing it updates without a refetch.
 *
 * Push supplements the coarse `data_changed` refresh rather than replacing it.
 * That refresh covers the fields and list-membership changes an entity push
 * structurally cannot carry — but only for writes made on this browser's own
 * app channel, which is why it is not a reconciliation floor for a rename
 * arriving from another device.
 *
 * Delivery is at-most-once with no replay, so this hook keeps its own narrow
 * reconciliation query behind both a slow poll and a refetch on every socket
 * reconnect. The reconnect covers the disconnect gap the server's own socket
 * age cap guarantees; the poll covers an event dropped while the socket
 * stayed up, which the reconnect would never see.
 *
 * @returns nothing; the hook exists for its subscription and cache effects.
 */
export const useFinancialAccountLiveUpdates = (): void => {
  const subscriptionsEnabled = useFeatureGate(FeatureGate.GraphqlSubscriptions);

  const { refetch, startPolling, stopPolling } = useQuery<
    FinancialAccountNamesQuery,
    FinancialAccountNamesQueryVariables
  >(FINANCIAL_ACCOUNT_NAMES, { skip: !subscriptionsEnabled, pollInterval: RECONCILE_POLL_INTERVAL_MS });

  useSubscription<FinancialAccountUpdatedSubscription, FinancialAccountUpdatedSubscriptionVariables>(
    FINANCIAL_ACCOUNT_UPDATED_SUBSCRIPTION,
    {
      skip: !subscriptionsEnabled,
      // A received event proves the push path is live and the cache fresh, so
      // restart the poll timer — the poll stays the reconciliation floor, but
      // it does not need to fire right after a push already delivered.
      onData: () => {
        stopPolling();
        startPolling(RECONCILE_POLL_INTERVAL_MS);
      },
      onError: (error) => {
        logger.warn(`[financial-account-live] subscription error: ${String(error)}`);
      },
    }
  );

  useWsReconnectEffect(subscriptionsEnabled, () => {
    void refetch().catch((error: unknown) => {
      logger.warn(`[financial-account-live] reconnect refetch failed: ${String(error)}`);
    });
  });
};
